'use client';
import { ParsedUrlQuery } from 'querystring';

import React, { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';

import { useTimer } from '@contexts/timer/TimerContext';
import { paramsToObject } from '@components/views/utils';
import {
  ResourceIdProvider,
  SearchURLProvider,
  SsrDataProvider,
} from '@api/data-loaders/resource';
import { IsKocAliemValidatedProvider } from '@contexts/loyalty/KocAilem';
import GeneralLoader from '@components/ui/GeneralLoader';

import HotelProvider from '../../hotel/detail/context';
import PackageDetail from './PackageDetail';
import { PackageContainer } from './styled';

const HotelDetail = dynamic(() => import('./package-hotel-tab'));

interface PackageDetailScreenProps {
  tabType?: string;
  metaInfo?: Record<string, string>;
  data?: any;
  initialData?: any;
  packageInitialData?: any;
}

const PackageDetailScreen = ({
  tabType,
  metaInfo,
  data,
  initialData,
  packageInitialData,
}: PackageDetailScreenProps) => {
  const [selectedTab, setSelectedTab] = useState('package');
  const [showFetchingAnimation, setShowFetchingAnimation] = useState(false);
  const [shouldResearchPackage, setShouldResearchPackage] = useState(false);
  const [isReloading, setIsReloading] = useState(false);

  const [showErrorModalPackageTimer, setShowErrorModalPackageTimer] =
    useState(false);
  const [showErrorModalHotelTimer, setShowErrorModalHotelTimer] =
    useState(false);
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const {
    timers,
    addTimer,
    removeTimer,
    subscribeTimerFinished,
    unsubscribeTimerFinished,
  } = useTimer();

  const handleAddTimer = (id: string) => {
    if (id) {
      addTimer(id);
    }
  };

  useEffect(() => {
    if (selectedTab === 'hotel') {
      setShouldResearchPackage(true);
    }
  }, [selectedTab]);
  useEffect(() => {
    if (tabType) {
      setSelectedTab(tabType);
    }
  }, [tabType]);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (packageInitialData.data) {
      queryClient.setQueryData(
        ['packageDetail', JSON.stringify(query)],
        packageInitialData.data
      );
    }
  }, [packageInitialData.data, query, queryClient]);

  useEffect(() => {
    if (initialData?.data) {
      queryClient.setQueryData(
        ['hotelDetail', JSON.stringify(query)],
        initialData?.data
      );
    }
  }, [initialData?.data, query, queryClient]);

  useEffect(() => {
    if (packageInitialData?.data?.shoppingFileId) {
      handleAddTimer(packageInitialData?.data?.shoppingFileId ?? '');
    }
    if (!packageInitialData?.isError) {
      setShowFetchingAnimation(false);
    }
  }, [packageInitialData]);

  useEffect(() => {
    if (
      initialData?.data?.rooms?.[0]?.providerInfo?.akgunlerProviderInfo
        ?.shoppingFileId
    ) {
      const listenerId = `listener${initialData?.data?.rooms?.[0]?.providerInfo?.akgunlerProviderInfo?.shoppingFileId}`;
      subscribeTimerFinished(
        listenerId,
        initialData?.data?.rooms?.[0].providerInfo?.akgunlerProviderInfo
          ?.shoppingFileId,
        handleTimerFinishedOnlyHotel
      );
      return () => {
        unsubscribeTimerFinished(listenerId);
      };
    }
  }, [initialData]);

  useEffect(() => {
    if (packageInitialData?.data?.shoppingFileId) {
      const listenerId = `listener${packageInitialData.data?.shoppingFileId}`;
      subscribeTimerFinished(
        listenerId,
        packageInitialData.data?.shoppingFileId,
        handleTimerFinished
      );
      return () => {
        unsubscribeTimerFinished(listenerId);
      };
    }
  }, [packageInitialData?.data]);

  const handleTimerFinishedOnlyHotel = (timerId) => {
    setShowErrorModalHotelTimer(true);
    removeTimer(timerId);
  };

  const handleTimerFinished = (timerId) => {
    setShowErrorModalPackageTimer(true);
    removeTimer(timerId);
  };

  const backToDetail = () => {
    setIsReloading(true);
    setShowErrorModalHotelTimer(false);
    setShowErrorModalPackageTimer(false);
    window.location.reload();
  };
  useEffect(() => {
    if (shouldResearchPackage && packageInitialData?.data?.shoppingFileId) {
      removeTimer(packageInitialData.data?.shoppingFileId);
    }
  }, [shouldResearchPackage]);
  return (
    <ResourceIdProvider value={data?.props?.resourceKey}>
      <SsrDataProvider value={data}>
        <SearchURLProvider value={data?.props?.urlSearch}>
          <IsKocAliemValidatedProvider>
            <PackageContainer>
              {isReloading ? <GeneralLoader /> : null}
              {selectedTab === 'package' ? (
                <HotelProvider>
                  <PackageDetail
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    packageData={packageInitialData.data}
                    isGetPackageDetailError={packageInitialData.isError}
                    showFetchingAnimation={showFetchingAnimation}
                    setShowFetchingAnimation={setShowFetchingAnimation}
                    shouldResearchPackage={shouldResearchPackage}
                    setShouldResearchPackage={setShouldResearchPackage}
                    onlyHotelData={initialData?.data}
                    metaInfo={metaInfo}
                    timers={timers}
                    showErrorModalPackageTimer={showErrorModalPackageTimer}
                    backToDetail={backToDetail}
                  />
                </HotelProvider>
              ) : (
                <HotelProvider>
                  <HotelDetail
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    data={initialData?.data}
                    isGetHotelDetailError={initialData?.isError}
                    metaInfo={metaInfo}
                    timers={timers}
                    showErrorModalHotelTimer={showErrorModalHotelTimer}
                    backToDetail={backToDetail}
                  />
                </HotelProvider>
              )}
            </PackageContainer>
          </IsKocAliemValidatedProvider>
        </SearchURLProvider>
      </SsrDataProvider>
    </ResourceIdProvider>
  );
};

export default PackageDetailScreen;
